import { Injectable } from '@angular/core';
import OF from 'src/app/models/OF';
import UnlinkedSession from 'src/app/models/UnlinkedSession';
import Work_Pause from 'src/app/models/Work_Pause';
import { environment } from 'src/environments/environment';
import { AuthService } from './auth.service';

const ConnectionStates = {
    connecting: 'connecting',
    connected: 'connected',
    disconnected: 'disconnected',
};

interface GlobalState {
    ofs: OF[];
    active_pauses: Work_Pause[];
    unlinked_sessions: UnlinkedSession[];
}

interface Identifiable {
    uid: string;
}

type JsonObject = Record<string, unknown>;
type WebsocketEventCallback = (data: unknown) => void;

const GLOBAL_RESYNC_RETRY_INITIAL_MS = 250;
const GLOBAL_RESYNC_RETRY_MAX_MS = 5000;

function isObject(value: unknown): value is JsonObject {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

@Injectable({
    providedIn: 'root',
})
export class WebsocketService extends EventTarget {
    socket!: WebSocket;

    status = ConnectionStates.disconnected;

    private globalState: GlobalState | null = null;
    private globalRevision: number | null = null;
    private globalEpoch: string | null = null;
    private globalGeneration: number | null = null;
    private retiredGlobalEpochs = new Set<string>();
    private globalResyncPending = false;
    private globalResyncHighWatermark: number | null = null;
    private globalResyncHighWatermarkEpoch: string | null = null;
    private globalResyncHighWatermarkGeneration: number | null = null;
    private globalResyncRetryDelay = GLOBAL_RESYNC_RETRY_INITIAL_MS;
    private globalResyncRetryTimer: ReturnType<typeof setTimeout> | null = null;
    private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    private reconnectResolve: ((reconnected: boolean) => void) | null = null;

    constructor(private authService: AuthService) {
        super();

        if (this.authService.authed) this.connect();

        this.authService.addEventListener('authed', () => this.connect());
        this.authService.addEventListener('refreshed', () => {
            if (this.status == ConnectionStates.disconnected) this.connect();
        });
        this.authService.addEventListener('unauthed', () => this.disconnect());
    }

    connect() {
        if (
            this.status == ConnectionStates.connected ||
            this.status == ConnectionStates.connecting
        )
            return;
        const accessToken = this.authService.getAccessToken();
        if (!accessToken) return;
        this.cancelSocketReconnect();
        this.stateChange(ConnectionStates.connecting);
        const wsStart = location.protocol == 'https:' ? 'wss://' : 'ws://';
        // set auth cookies
        console.debug('[Socket]', 'Connecting...');
        if (this.socket) {
            this.finishGlobalResync();
            this.socket.onopen = null;
            this.socket.onmessage = null;
            this.socket.onclose = null;
            this.socket.onerror = null;
            this.socket.close();
        }
        const socket = this.createSocket(
            `${wsStart}${environment.WS_URL}/ws/?${accessToken}`
        );
        this.socket = socket;
        socket.onopen = () => {
            if (this.socket === socket) this.onSocketOpened();
        };
        socket.onmessage = event => {
            if (this.socket === socket) this.onSocketMessage(event);
        };
        socket.onclose = () => {
            if (this.socket === socket) this.onSocketClosed();
        };
        socket.onerror = () => {
            if (this.socket === socket) this.onSocketErrored();
        };
        this.shouldReconnect = true;
    }

    private createSocket(url: string): WebSocket {
        return new WebSocket(url);
    }

    disconnect() {
        this.shouldReconnect = false;
        this.cancelSocketReconnect();
        this.finishGlobalResync();
        if (this.socket) this.socket.close();
    }

    reconnecting = false;
    reconnectSocket(t = 1000) {
        return new Promise(resolve => {
            if (this.reconnecting) return resolve(false);
            this.reconnecting = true;
            this.reconnectResolve = resolve;
            console.debug('[Socket]', `Reconnecting in ${t}ms`);
            this.reconnectTimer = setTimeout(() => {
                this.reconnectTimer = null;
                this.reconnectResolve = null;
                this.reconnecting = false;
                if (!this.shouldReconnect) return resolve(false);
                console.debug('[Socket]', 'Reconnecting...');
                this.connect();
                resolve(true);
            }, t);
        });
    }

    private cancelSocketReconnect() {
        if (this.reconnectTimer !== null) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }
        const resolve = this.reconnectResolve;
        this.reconnectResolve = null;
        this.reconnecting = false;
        resolve?.(false);
    }

    sendSocketMessage(message: unknown) {
        if (message === 'global:resync') {
            this.requestGlobalResync();
            return;
        }

        this.sendSocketMessageNow(message);
    }

    private sendSocketMessageNow(message: unknown): boolean {
        if (this.socket?.readyState !== WebSocket.OPEN) return false;

        try {
            this.socket.send(JSON.stringify(message));
            return true;
        } catch (error) {
            console.error('[Socket]', 'Could not send message', error);
            return false;
        }
    }

    onSocketMessage(e: MessageEvent<string>) {
        let message: unknown;
        try {
            message = JSON.parse(e.data);
        } catch (error) {
            console.error('[Socket]', 'Invalid message received', error);
            return;
        }

        if (!isObject(message) || typeof message['type'] !== 'string') return;

        console.debug('[Socket]', 'Message received');

        const contentData = this.decodeData(message['data']);

        switch (message['type']) {
            case 'metrics_update':
                this.triggerWSEvent('metrics', contentData);
                break;
            case 'unlinkeds:resync':
            case 'unlinkeds:state_update':
                this.triggerWSEvent('unlinkeds:updated', contentData);
                break;
            case 'unlinkeds:created':
                this.triggerWSEvent('unlinkeds:created', contentData);
                break;
            case 'global:resync':
            case 'global:state_update':
                this.acceptLegacyGlobalState(contentData, 'global:updated');
                break;
            case 'of:created':
                this.acceptLegacyGlobalState(contentData, 'of:created');
                break;
            case 'global:snapshot':
                this.acceptGlobalSnapshot(message, contentData);
                break;
            case 'global:delta':
                this.acceptGlobalDelta(message, contentData);
                break;
        }
    }

    private decodeData(data: unknown): unknown {
        if (typeof data !== 'string') return data;

        try {
            return JSON.parse(data);
        } catch {
            return data;
        }
    }

    private acceptLegacyGlobalState(data: unknown, event: string) {
        if (!this.isGlobalState(data)) {
            this.scheduleGlobalResyncRetry();
            return;
        }

        this.globalState = data;
        this.globalRevision = null;
        this.globalEpoch = null;
        this.globalGeneration = null;
        this.finishGlobalResync();
        this.triggerWSEvent(event, data);
    }

    private acceptGlobalSnapshot(message: JsonObject, data: unknown) {
        const revision = message['revision'];
        const epochValue = message['epoch'];
        const generationValue = message['generation'];
        if (
            message['protocol'] !== 2 ||
            !this.isRevision(revision) ||
            !this.isOptionalEpoch(epochValue) ||
            !this.isOptionalGeneration(generationValue) ||
            !this.isGlobalState(data)
        ) {
            // A broken reply must not cause a tight request/reply loop if the
            // server keeps returning the same malformed snapshot.
            this.scheduleGlobalResyncRetry();
            return;
        }

        const epoch = epochValue ?? null;
        const generation = generationValue ?? null;
        if (
            generation === null &&
            epoch !== null &&
            this.retiredGlobalEpochs.has(epoch)
        )
            return;

        if (
            generation !== null &&
            this.globalGeneration !== null &&
            generation < this.globalGeneration
        )
            return;

        const changesGeneration =
            generation !== null &&
            (this.globalGeneration === null ||
                generation > this.globalGeneration);
        const sameGeneration =
            generation !== null &&
            this.globalGeneration !== null &&
            generation === this.globalGeneration;
        if (
            sameGeneration &&
            epoch !== null &&
            this.globalEpoch !== null &&
            epoch !== this.globalEpoch
        ) {
            this.rejectGlobalSnapshot();
            return;
        }

        const changesLegacyEpoch =
            generation === null && epoch !== null && epoch !== this.globalEpoch;
        const establishesNewBaseline = changesGeneration || changesLegacyEpoch;
        const effectiveGeneration = generation ?? this.globalGeneration;

        // A delta may overtake the direct snapshot reply while a resync is in
        // flight. Such a delta cannot be applied without its missing baseline,
        // but its revision tells us how fresh that baseline must be.
        if (
            this.globalResyncPending &&
            this.globalResyncHighWatermark !== null
        ) {
            const highGeneration = this.globalResyncHighWatermarkGeneration;
            const highEpoch = this.globalResyncHighWatermarkEpoch;
            let missesHighWatermark = false;

            if (highGeneration !== null) {
                missesHighWatermark =
                    effectiveGeneration === null ||
                    effectiveGeneration < highGeneration ||
                    (effectiveGeneration === highGeneration &&
                        ((highEpoch !== null &&
                            epoch !== null &&
                            epoch !== highEpoch) ||
                            revision < this.globalResyncHighWatermark));
            } else {
                const waitsForDifferentEpoch =
                    highEpoch !== null && highEpoch !== this.globalEpoch;
                missesHighWatermark =
                    (waitsForDifferentEpoch && epoch !== highEpoch) ||
                    ((!establishesNewBaseline || waitsForDifferentEpoch) &&
                        revision < this.globalResyncHighWatermark);
            }

            if (missesHighWatermark) {
                this.scheduleGlobalResyncRetry();
                return;
            }
        }

        // Broadcasts and direct resync replies originate in different server
        // threads. Never let a delayed snapshot roll back a delta that this
        // connection has already applied.
        if (
            !establishesNewBaseline &&
            this.globalRevision !== null &&
            revision < this.globalRevision
        ) {
            if (this.globalResyncPending) {
                this.scheduleGlobalResyncRetry();
            }
            return;
        }

        this.globalState = data;
        this.globalRevision = revision;
        if (
            epoch !== null &&
            this.globalEpoch !== null &&
            epoch !== this.globalEpoch
        ) {
            this.retiredGlobalEpochs.add(this.globalEpoch);
        }
        // During a rolling deployment an older publisher can omit epoch while
        // still sharing the same Redis revision sequence. Such a snapshot is
        // legacy-compatible and must not erase an epoch already negotiated.
        if (epoch !== null) {
            this.retiredGlobalEpochs.delete(epoch);
            this.globalEpoch = epoch;
        }
        if (generation !== null) this.globalGeneration = generation;
        this.finishGlobalResync();
        this.triggerWSEvent('global:updated', data);
    }

    private acceptGlobalDelta(message: JsonObject, data: unknown) {
        const baseRevision = message['base_revision'];
        const revision = message['revision'];
        const event = message['event'];
        const epochValue = message['epoch'];
        const generationValue = message['generation'];

        if (
            message['protocol'] !== 2 ||
            !this.isRevision(baseRevision) ||
            !this.isRevision(revision) ||
            !this.isOptionalEpoch(epochValue) ||
            !this.isOptionalGeneration(generationValue) ||
            (event !== 'global:state_update' && event !== 'of:created') ||
            !isObject(data)
        ) {
            this.rejectGlobalUpdate();
            return;
        }

        const epoch = epochValue ?? null;
        const generation = generationValue ?? null;
        if (
            generation === null &&
            epoch !== null &&
            this.retiredGlobalEpochs.has(epoch)
        )
            return;

        if (
            generation !== null &&
            this.globalGeneration !== null &&
            generation < this.globalGeneration
        )
            return;

        if (
            generation !== null &&
            (this.globalGeneration === null ||
                generation > this.globalGeneration)
        ) {
            this.rememberGlobalRevision(revision, epoch, generation);
            this.rejectGlobalUpdate();
            return;
        }

        if (
            generation !== null &&
            generation === this.globalGeneration &&
            epoch !== null &&
            this.globalEpoch !== null &&
            epoch !== this.globalEpoch
        ) {
            // Same generation with two different epochs is an inconsistent
            // envelope, not a newer baseline. Resync without contaminating the
            // high-watermark with the conflicting identity.
            this.rejectGlobalSnapshot();
            return;
        }

        // Epoch-bearing deltas are only meaningful on top of a snapshot from
        // that exact epoch. Epoch-less messages retain the original v2 contract
        // so old backends remain compatible during a rolling deployment.
        if (
            generation === null &&
            epoch !== null &&
            epoch !== this.globalEpoch
        ) {
            this.rememberGlobalRevision(revision, epoch, generation);
            this.rejectGlobalUpdate();
            return;
        }

        // A duplicate delta is harmless. Re-applying it could reorder new
        // entities, while requesting a snapshot would waste bandwidth. This
        // also covers semantic-only events published at the current revision.
        if (this.globalRevision !== null && revision <= this.globalRevision) {
            return;
        }

        const advancesRevision = revision === baseRevision + 1;
        const repeatsRevision = revision === baseRevision;
        if (!advancesRevision && !repeatsRevision) {
            this.rejectGlobalUpdate();
            return;
        }

        // Once a gap or malformed patch has been detected, only a fresh
        // snapshot can establish a trustworthy baseline. Remember deltas that
        // race that snapshot so an older reply is not accepted as current.
        if (this.globalResyncPending) {
            this.rememberGlobalRevision(revision, epoch, generation);
            return;
        }

        // A semantic-only event at an unknown revision cannot update the
        // local baseline. Fetch that baseline instead.
        if (!advancesRevision) {
            this.rememberGlobalRevision(revision, epoch, generation);
            this.rejectGlobalUpdate();
            return;
        }

        if (
            this.globalState === null ||
            this.globalRevision === null ||
            baseRevision !== this.globalRevision
        ) {
            this.rememberGlobalRevision(revision, epoch, generation);
            this.rejectGlobalUpdate();
            return;
        }

        const ofs = this.applyCollectionDelta(
            this.globalState.ofs,
            data['ofs']
        );
        const activePauses = this.applyCollectionDelta(
            this.globalState.active_pauses,
            data['active_pauses']
        );
        const unlinkedSessions = this.applyCollectionDelta(
            this.globalState.unlinked_sessions,
            data['unlinked_sessions']
        );

        if (
            ofs === null ||
            activePauses === null ||
            unlinkedSessions === null
        ) {
            this.rejectGlobalUpdate();
            return;
        }

        const nextState: GlobalState = {
            ofs,
            active_pauses: activePauses,
            unlinked_sessions: unlinkedSessions,
        };

        this.globalState = nextState;
        this.globalRevision = revision;
        if (epoch !== null && this.globalEpoch === null) {
            this.retiredGlobalEpochs.delete(epoch);
            this.globalEpoch = epoch;
        }
        this.finishGlobalResync();
        if (event === 'of:created') {
            this.triggerWSEvent('of:created', nextState);
        } else {
            this.triggerWSEvent('global:updated', nextState);
        }
    }

    private applyCollectionDelta<T extends Identifiable>(
        current: T[],
        value: unknown
    ): T[] | null {
        // Missing collections are unchanged. This keeps the reducer compatible
        // with servers that omit empty patches to save a few more bytes.
        if (value === undefined) return current;
        if (!isObject(value)) return null;

        const upsert = value['upsert'];
        const remove = value['remove'];
        if (
            !Array.isArray(upsert) ||
            !upsert.every(item => this.isIdentifiable(item)) ||
            !Array.isArray(remove) ||
            !remove.every(uid => typeof uid === 'string')
        ) {
            return null;
        }

        const byUid = new Map(current.map(item => [item.uid, item]));
        for (const uid of remove) byUid.delete(uid);
        for (const item of upsert) byUid.set(item.uid, item as T);
        return Array.from(byUid.values());
    }

    private isGlobalState(value: unknown): value is GlobalState {
        if (!isObject(value)) return false;

        return (
            Array.isArray(value['ofs']) &&
            value['ofs'].every(item => this.isIdentifiable(item)) &&
            Array.isArray(value['active_pauses']) &&
            value['active_pauses'].every(item => this.isIdentifiable(item)) &&
            Array.isArray(value['unlinked_sessions']) &&
            value['unlinked_sessions'].every(item => this.isIdentifiable(item))
        );
    }

    private isIdentifiable(value: unknown): value is Identifiable {
        return isObject(value) && typeof value['uid'] === 'string';
    }

    private isRevision(value: unknown): value is number {
        return (
            typeof value === 'number' &&
            Number.isSafeInteger(value) &&
            value >= 0
        );
    }

    private isOptionalEpoch(value: unknown): value is string | undefined {
        return (
            value === undefined ||
            (typeof value === 'string' && value.trim() !== '')
        );
    }

    private isOptionalGeneration(value: unknown): value is number | undefined {
        return value === undefined || this.isRevision(value);
    }

    private rejectGlobalUpdate() {
        this.requestGlobalResync();
    }

    private rejectGlobalSnapshot() {
        if (this.globalResyncPending) this.scheduleGlobalResyncRetry();
        else this.requestGlobalResync();
    }

    private rememberGlobalRevision(
        revision: number,
        epoch: string | null,
        generation: number | null
    ) {
        const effectiveGeneration = generation ?? this.globalGeneration;
        if (
            this.globalResyncHighWatermark === null ||
            (effectiveGeneration !== null &&
                (this.globalResyncHighWatermarkGeneration === null ||
                    effectiveGeneration >
                        this.globalResyncHighWatermarkGeneration))
        ) {
            this.globalResyncHighWatermark = revision;
            this.globalResyncHighWatermarkEpoch = epoch;
            this.globalResyncHighWatermarkGeneration = effectiveGeneration;
            return;
        }

        if (
            this.globalResyncHighWatermarkGeneration !== null &&
            (effectiveGeneration === null ||
                effectiveGeneration < this.globalResyncHighWatermarkGeneration)
        )
            return;

        if (
            this.globalResyncHighWatermarkEpoch !== null &&
            epoch !== null &&
            this.globalResyncHighWatermarkEpoch !== epoch
        ) {
            // With an ordered generation, conflicting epochs are malformed and
            // must not replace the watermark. Epoch-only legacy traffic keeps
            // its previous last-message-wins fallback.
            if (effectiveGeneration !== null) return;
            this.globalResyncHighWatermark = revision;
            this.globalResyncHighWatermarkEpoch = epoch;
            return;
        }

        this.globalResyncHighWatermark = Math.max(
            this.globalResyncHighWatermark,
            revision
        );
        if (this.globalResyncHighWatermarkEpoch === null && epoch !== null) {
            this.globalResyncHighWatermarkEpoch = epoch;
        }
    }

    private finishGlobalResync() {
        this.globalResyncPending = false;
        this.globalResyncHighWatermark = null;
        this.globalResyncHighWatermarkEpoch = null;
        this.globalResyncHighWatermarkGeneration = null;
        this.globalResyncRetryDelay = GLOBAL_RESYNC_RETRY_INITIAL_MS;
        if (this.globalResyncRetryTimer !== null) {
            clearTimeout(this.globalResyncRetryTimer);
            this.globalResyncRetryTimer = null;
        }
    }

    private scheduleGlobalResyncRetry() {
        this.globalResyncPending = true;
        if (this.globalResyncRetryTimer !== null) return;

        const delay = this.globalResyncRetryDelay;
        this.globalResyncRetryDelay = Math.min(
            delay * 2,
            GLOBAL_RESYNC_RETRY_MAX_MS
        );
        this.globalResyncRetryTimer = setTimeout(() => {
            this.globalResyncRetryTimer = null;
            this.globalResyncPending = false;
            if (this.socket?.readyState === WebSocket.OPEN) {
                this.requestGlobalResync();
            }
        }, delay);
    }

    private requestGlobalResync() {
        if (this.globalResyncPending) return;
        this.globalResyncPending = true;
        // Keep the legacy string request during the rolling deployment: old
        // backends understand it, while v2 backends select the response format
        // from the client:hello sent when the connection opened.
        if (!this.sendSocketMessageNow('global:resync')) {
            // OPEN can race a transport failure and WebSocket.send() may throw.
            // Do not leave the request latched forever without an in-flight
            // message; use the same bounded backoff as malformed responses.
            this.scheduleGlobalResyncRetry();
        }
    }

    onSocketOpened() {
        console.debug('[Socket]', 'Connected');
        this.cancelSocketReconnect();
        // A revision only has meaning inside one uninterrupted connection. Keep
        // rendering the last state while reconnecting, but require a fresh
        // snapshot before accepting more deltas.
        this.globalRevision = null;
        this.globalEpoch = null;
        this.globalGeneration = null;
        this.retiredGlobalEpochs.clear();
        this.finishGlobalResync();
        this.sendSocketMessageNow({
            type: 'client:hello',
            client: 'workers',
            protocol: 2,
        });
        this.requestGlobalResync();
        this.triggerEvent('connect');
        this.stateChange(ConnectionStates.connected);
    }

    shouldReconnect = true;
    onSocketClosed() {
        console.debug('[Socket]', 'Socket connection closed');
        this.finishGlobalResync();
        this.notifySocketDisconnected();
        if (this.shouldReconnect) this.reconnectSocket();
    }

    onSocketErrored() {
        console.error('[Socket]', 'Socket connection errored');
        this.notifySocketDisconnected();
        if (
            this.socket?.readyState === WebSocket.CONNECTING ||
            this.socket?.readyState === WebSocket.OPEN
        )
            this.socket.close();
        if (this.shouldReconnect) this.reconnectSocket();
    }

    private notifySocketDisconnected() {
        if (this.status === ConnectionStates.disconnected) return;
        this.triggerEvent('disconnect');
        this.stateChange(ConnectionStates.disconnected);
    }

    stateChange(state: string) {
        this.status = state;
        this.triggerEvent('state-change', state);
    }

    triggerEvent(event: string, data: unknown = null) {
        const eventObject = new CustomEvent(event, { detail: data });
        this.dispatchEvent(eventObject);
    }

    eventHandlers: Record<string, WebsocketEventCallback[]> = {};
    on(events: string, callback: WebsocketEventCallback) {
        for (const event of events.split(',')) {
            if (!this.eventHandlers[event]) this.eventHandlers[event] = [];
            if (this.eventHandlers[event].includes(callback)) return;

            this.eventHandlers[event].push(callback);
        }

        return () => this.off(events, callback);
    }

    off(events: string, callback: WebsocketEventCallback) {
        for (const event of events.split(',')) {
            if (!this.eventHandlers[event]) return;
            if (!this.eventHandlers[event].includes(callback)) return;

            this.eventHandlers[event].splice(
                this.eventHandlers[event].indexOf(callback),
                1
            );
        }
    }

    triggerWSEvent(event: string, data: unknown = null) {
        if (!this.eventHandlers[event]) return;
        for (const callback of this.eventHandlers[event]) callback(data);
    }

    log(...args: unknown[]) {
        console.debug('[WS]', ...args);
    }
}

export default WebsocketService;
